import React from 'react';
import {
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
  ValueOptions,
} from '@mui/x-data-grid-pro';
import ReportStatus from '@components/ReportStatus';
import { ReportStatus as ReportStatusType } from '@declarations/common/enums/ReportStatus';
import { User } from '@declarations/models/User';
import { Incident } from '@declarations/models/reports/Incident';
import { UserType } from '@declarations/common/enums/UserType';
import { filterRequiredOperators } from '@components/DataGrid/utils/gridOperators';
import ReportStatusOptions from '@constants/options/ReportStatusOptions';
import RiskLevelOptions from '@constants/options/RiskLeveOptions';
import getOptionLabel from '@utils/getOptionLabel';
import IncidentTypeOptions from '@constants/options/IncidentTypeOptions';
import routes from '@constants/routes';
import EditableGridHeader from '@components/EditableGridHeader';
import expertUserRender from '@components/DataGrid/renders/expertUserRender';
import expertUserHeaderRender from '@components/DataGrid/renders/expertUserHeaderRender';
import linkedReportRender from '@components/DataGrid/renders/linkedReportRender';
import { RequestingPersonLabels } from '@constants/options/RequestingPersonOptions';
import { RequestedMedicalRecordsPerson } from '@declarations/common/enums/RequestedMedicalRecordsPerson';
import yesNoRender from '@components/DataGrid/renders/yesNoRender';
import YesNoOptions from '@constants/options/YesNoOptions';
import IdLinkNewTab from '@components/IdLinkNewTab';

type GetColumnsProps = {
  userType: UserType,
  clinicalReviewers?: ValueOptions[];
  legalReviewers?: ValueOptions[];
  inlineEditEnabled: boolean;
};

const incidentReportsColumns =
  ({
     userType,
     clinicalReviewers,
     legalReviewers,
     inlineEditEnabled,
   }: GetColumnsProps): GridColDef<Incident>[] => {
    const stringFilterOperators = filterRequiredOperators(getGridStringOperators());
    const dateFilterOperators = filterRequiredOperators(getGridDateOperators());

    const columns: GridColDef<Incident>[] = [
      {
        field: 'reportNumber',
        headerName: 'Report number',
        description: 'Report number',
        minWidth: 140,
        renderCell: (params: GridRenderCellParams<Incident>) => (
          <IdLinkNewTab
            id={params.row.id}
            href={routes.incidentReportForm}
            value={params.row.reportNumber}
          />
        ),
        filterOperators: stringFilterOperators,
      },
      {
        field: 'linkedReports.medicalReport.reportNumber',
        headerName: 'Linked MRR',
        description: 'Linked Medical Record Request',
        minWidth: 80,
        align: 'center',
        renderCell: linkedReportRender<Incident>({
          href: routes.medicalRecordRequestForm,
          getId: row => row.linkedReports?.medicalReport?.id,
          getDate: row => row.linkedReports?.medicalReport?.requestDate,
          getReportNumber: row => row.linkedReports?.medicalReport?.reportNumber,
          getAdditionalField: row => row.linkedReports?.medicalReport?.requestedRecordsPerson
            && RequestingPersonLabels[row.linkedReports.medicalReport.requestedRecordsPerson as RequestedMedicalRecordsPerson],
        }),
        filterOperators: filterRequiredOperators(getGridStringOperators()),
      },
      {
        field: 'linkedReports.lawsuit.reportNumber',
        headerName: 'Linked Lawsuit',
        minWidth: 80,
        align: 'center',
        renderCell: linkedReportRender<Incident>({
          href: routes.lawsuitForm,
          getId: row => row.linkedReports?.lawsuit?.id,
          getDate: row => row.linkedReports?.lawsuit?.noticeDate,
          getReportNumber: row => row.linkedReports?.lawsuit?.reportNumber,
          getAdditionalField: row => row.linkedReports?.lawsuit?.caseName,
        }),
        filterOperators: filterRequiredOperators(getGridStringOperators()),
      },
      {
        field: 'clinicalReviewer',
        headerName: 'Clinical Reviewer',
        editable: inlineEditEnabled,
        type: 'singleSelect',
        minWidth: 160,
        renderCell: expertUserRender(inlineEditEnabled),
        renderHeader: expertUserHeaderRender(inlineEditEnabled),
        valueOptions: clinicalReviewers,
        filterable: false,
        sortable: false,
      },
      {
        field: 'legalReviewer',
        headerName: 'Legal Reviewer',
        editable: inlineEditEnabled,
        type: 'singleSelect',
        minWidth: 160,
        renderCell: expertUserRender(inlineEditEnabled),
        renderHeader: expertUserHeaderRender(inlineEditEnabled),
        valueOptions: legalReviewers,
        filterable: false,
        sortable: false,
      },
      {
        field: 'createdAt',
        headerName: 'Date',
        minWidth: 90,
        valueGetter: (params: GridValueGetterParams<Incident>) => new Date(params.row.createdAt),
        renderCell: (params: GridRenderCellParams<Incident>) => new Date(params.row.createdAt).toLocaleDateString('en-US'),
        type: 'date',
        filterOperators: dateFilterOperators,
      },
      {
        field: 'residentFirstName',
        headerName: 'Resident First Name',
        minWidth: 140,
        filterOperators: stringFilterOperators,
      },
      {
        field: 'residentLastName',
        headerName: 'Resident Last Name',
        minWidth: 140,
        filterOperators: stringFilterOperators,
      },
      {
        field: 'residentMedicalRecordNumber',
        headerName: 'Med. Rec. No',
        description: 'Medical Record Number',
        minWidth: 110,
        filterOperators: stringFilterOperators,
      },
      {
        field: 'incidentDate',
        headerName: 'Date of Incident',
        minWidth: 120,
        valueGetter: (params: GridValueGetterParams<Incident>) => new Date(params.row.incidentDate),
        renderCell: (params: GridRenderCellParams<Incident>) => new Date(params.row.incidentDate).toLocaleDateString('en-US'),
        type: 'date',
        filterOperators: dateFilterOperators,
      },
      {
        field: 'typeOfIncident',
        headerName: 'Type of Incident',
        minWidth: 130,
        renderCell: (params: GridRenderCellParams<Incident>) =>
          <span>{getOptionLabel(IncidentTypeOptions, params.value)}</span>,
        type: 'singleSelect',
        valueOptions: IncidentTypeOptions,
        filterOperators: filterRequiredOperators(getGridSingleSelectOperators()),
      },
      {
        field: 'userSnapshot',
        headerName: 'Reporter',
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<Incident, User>) =>
          <span>{[params.value?.firstname, params.value?.lastname].join(' ')}</span>,
        filterable: false,
      },
      {
        field: 'userSnapshot.email',
        headerName: 'Email',
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<Incident, User>) => <span>{params.row.userSnapshot?.email}</span>,
        filterOperators: stringFilterOperators,
      },
      {
        field: 'userSnapshot.preferredContactNumber',
        headerName: 'Phone No',
        description: 'Phone Number',
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<Incident, User>) => params.row.userSnapshot?.preferredContactNumber,
        filterOperators: stringFilterOperators,
      },
      {
        field: 'facility',
        headerName: 'Facility',
        minWidth: 120,
        filterOperators: undefined,
      },
      {
        field: 'operator',
        headerName: 'Operator',
        minWidth: 120,
        filterOperators: undefined,
      },
      {
        field: 'city',
        headerName: 'City',
        minWidth: 100,
      },
      {
        field: 'state',
        headerName: 'State',
        minWidth: 80,
      },
      {
        field: 'riskPoints',
        headerName: 'Risk Level',
        minWidth: 95,
        renderCell: (params: GridRenderCellParams<Incident>) => {
          if (params.value <= 6) {
            return <span title={'Low'}>Low</span>;
          }
          if (params.value <= 9) {
            return <span title={'Medium'}>Medium</span>;
          }
          return <span title={'High'}>High</span>;
        },
        type: 'singleSelect',
        valueOptions: RiskLevelOptions,
        filterable: false,
      },
      inlineEditEnabled ?
        ({
          field: 'calculatedStatus',
          headerName: 'Status',
          editable: true,
          renderHeader: (headerParam) => <EditableGridHeader title={headerParam.colDef.headerName!}/>,
          minWidth: 150,
          renderCell: (params: GridRenderCellParams<Incident, ReportStatusType>) => (
            <ReportStatus calculatedStatus={params.row.calculatedStatus} forcedStatus={params.row.forcedStatus}
                          editable={true}/>
          ),
          type: 'singleSelect',
          valueOptions: (gridParams) => {
            if (!gridParams.id) {
              return ReportStatusOptions;
            }
            return [{value: null, label: 'AUTO'}, ...ReportStatusOptions];
          },
          filterable: false,
          sortable: false
        }) : ({
          field: 'status',
          headerName: 'Status',
          editable: false,
          minWidth: 125,
          renderCell: (params: GridRenderCellParams<Incident, ReportStatusType>) => (
            <ReportStatus calculatedStatus={params.row.calculatedStatus} forcedStatus={params.row.forcedStatus}
                          editable={false}/>
          ),
          type: 'singleSelect',
          valueOptions: ReportStatusOptions,
          filterable: false,
          sortable: false
        }),
      {
        field: 'clinicalReviewCompleted',
        minWidth: 100,
        headerName: 'Clinical Rev. Completed',
        description: 'Clinical Review Completed',
        renderCell: yesNoRender,
        type: 'singleSelect',
        valueOptions: YesNoOptions,
        filterOperators: filterRequiredOperators(getGridSingleSelectOperators()),
      },
      {
        field: 'legalReviewCompleted',
        minWidth: 100,
        headerName: `Legal Rev. Competed`,
        description: 'Legal Review Completed',
        renderCell: yesNoRender,
        type: 'singleSelect',
        valueOptions: YesNoOptions,
        filterOperators: filterRequiredOperators(getGridSingleSelectOperators()),
      },
    ];

    if (userType === UserType.SuperAdmin) {
      columns.push(
        {
          field: 'comment',
          headerName: 'Comment',
          renderHeader: () => inlineEditEnabled ? <EditableGridHeader title="Comment"/> : 'Comment',
          minWidth: 140,
          editable: inlineEditEnabled,
        },
      );
    }

    return columns.map((column) => ({
      ...column,
      renderCell: column.renderCell || ((params: GridRenderCellParams<Incident>) => {
        const text = params.row[column.field as keyof Incident] as string;
        return (<span title={text}>{text}</span>);
      }),
    }));
  };

export default incidentReportsColumns;
